import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Users, ClipboardCheck, TrendingUp, 
  Clock
} from 'lucide-react';
import axios from 'axios';
import TeacherSidebar, { TEACHER_TAB_GROUPS } from '@/components/TeacherSidebar';
import InstallAppBanner from '@/components/InstallAppBanner';

// Tab content components
import OverviewTab from '@/components/teacher/OverviewTab';
import ProficiencyWatchDigest from '@/components/widget/ProficiencyWatchDigest';
import GradingTab from '@/components/teacher/GradingTab';
import RecentResultsTab from '@/components/teacher/RecentResultsTab';
import TeacherResourcesTab from '@/components/teacher/TeacherResourcesTab';
import ExamIntegrityTab from '@/components/teacher/ExamIntegrityTab';
import ProficiencyTrainingModule from '@/components/ProficiencyTrainingModule';

// Already-extracted standalone components
import ClassManagement from '@/components/ClassManagement';
import CustomQuestionCreator from '@/components/CustomQuestionCreator';
import AnalyticsDashboard from '@/components/AnalyticsDashboard';
import DemoDataManager from '@/components/DemoDataManager';
import AssessmentWindows from '@/components/AssessmentWindows';
import GrowthTracker from '@/components/GrowthTracker';
import AssignmentManager from '@/components/AssignmentManager';
import CohortComparison from '@/components/CohortComparison';
import PredictiveProjections from '@/components/PredictiveProjections';
import BetaFeedbackSurvey from '@/components/BetaFeedbackSurvey';
import LessonPlanTab from '@/components/teacher/LessonPlanTab';
import QuickChecksTab from '@/components/teacher/QuickChecksTab';
import TeacherToolkitTab from '@/components/teacher/TeacherToolkitTab';
import TeacherROIBanner from '@/components/teacher/TeacherROIBanner';
import CrosswalkTool from '@/components/teacher/CrosswalkTool';
import ClassGroupsReport from '@/components/teacher/ClassGroupsReport';
import MosaicHero from '@/components/mosaic/MosaicHero';
import MosaicComplianceCard from '@/components/mosaic/MosaicComplianceCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeacherDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [classes, setClasses] = useState([]);
  const [pendingGrading, setPendingGrading] = useState([]);
  const [initialGraded, setInitialGraded] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const [flaggedSessions, setFlaggedSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  // v6.32.102 — Seed activeTab from `?tab=` query param so deep links
  // (e.g. /teacher?tab=toolkit from the Products dropdown) land on the
  // requested tab instead of Overview.
  const initialTabFromUrl = searchParams.get('tab');
  const validTabValues = TEACHER_TAB_GROUPS.flatMap((g) => g.tabs.map((t) => t.value));
  const [activeTab, setActiveTab] = useState(
    initialTabFromUrl && validTabValues.includes(initialTabFromUrl) ? initialTabFromUrl : 'overview'
  );
  const [classInitialView, setClassInitialView] = useState('classes');
  const [activeGrowthClass, setActiveGrowthClass] = useState(null);

  // Keep URL in sync with the active tab so users can bookmark and
  // share deep links to specific tabs.
  useEffect(() => {
    if (activeTab && activeTab !== 'overview') {
      if (searchParams.get('tab') !== activeTab) {
        setSearchParams({ tab: activeTab }, { replace: true });
      }
    } else if (searchParams.get('tab')) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (classes.length > 0 && !activeGrowthClass) {
      setActiveGrowthClass(classes[0].class_id);
    }
  }, [classes]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classesRes, resultsRes, flaggedRes, pendingRes] = await Promise.all([
          axios.get(`${API}/classes`),
          axios.get(`${API}/results`),
          axios.get(`${API}/exam-integrity/flagged-sessions`).catch(() => ({ data: { flagged_sessions: [] } })),
          axios.get(`${API}/pending-grading`).catch(() => ({ data: { pending: [], graded: [] } }))
        ]);
        
        setClasses(classesRes.data.classes || []);
        setRecentResults(resultsRes.data.results?.slice(0, 10) || []);
        setFlaggedSessions(flaggedRes.data.flagged_sessions || []);
        setPendingGrading(pendingRes.data.pending || []);
        setInitialGraded(pendingRes.data.graded || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-6 pt-16 md:pt-8 pb-8 md:flex md:gap-8" data-testid="teacher-dashboard">
      {/* v6.32.102 — Left sidebar nav (replaces v6.32.101 horizontal 21-tab strip). v6.34.14 — breakpoint lowered from lg≥1024 to md≥768 so tablets get the full sidebar. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="md:flex md:gap-8 md:flex-1 md:items-start">
        <TeacherSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          badges={{
            grading: pendingGrading.length > 0 ? pendingGrading.length : undefined,
            integrity: flaggedSessions.length > 0 ? flaggedSessions.length : undefined,
          }}
        />

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Teacher Dashboard
            </h1>
            <p className="text-gray-600">
              Welcome back, {user?.name || 'Teacher'}
            </p>
          </div>

          {/* ROI Banner — v6.24.0 */}
          <TeacherROIBanner />

          {/* v6.32.162 — MOSAIC Three-Moment Hero (Screen → Monitor → Benchmark) */}
          <MosaicHero onJumpTo={setActiveTab} />

          {/* v6.32.163 — MOSAIC Compliance Snapshot */}
          <div className="mb-8">
            <MosaicComplianceCard />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-t border-l border-gray-300 mb-8">
            <div className="swiss-card cursor-pointer hover:bg-blue-50 transition-colors" onClick={() => { setClassInitialView('classes'); setActiveTab('classes'); }} data-testid="stat-my-classes">
              <Users className="w-8 h-8 text-blue-600 mb-3" />
              <div className="text-3xl font-bold">{classes.length}</div>
              <div className="text-sm text-gray-500">My Classes</div>
            </div>
            <div className="swiss-card cursor-pointer hover:bg-green-50 transition-colors" onClick={() => { setClassInitialView('students'); setActiveTab('classes'); }} data-testid="stat-total-students">
              <ClipboardCheck className="w-8 h-8 text-green-600 mb-3" />
              <div className="text-3xl font-bold">
                {classes.reduce((sum, c) => sum + (c.student_ids?.length || 0), 0)}
              </div>
              <div className="text-sm text-gray-500">Total Students</div>
            </div>
            <div className="swiss-card cursor-pointer hover:bg-orange-50 transition-colors" onClick={() => setActiveTab('grading')} data-testid="stat-pending-grading">
              <Clock className="w-8 h-8 text-orange-600 mb-3" />
              <div className="text-3xl font-bold">{pendingGrading.length}</div>
              <div className="text-sm text-gray-500">Pending Grading</div>
            </div>
            <div className="swiss-card cursor-pointer hover:bg-purple-50 transition-colors" onClick={() => setActiveTab('results')} data-testid="stat-avg-score">
              <TrendingUp className="w-8 h-8 text-purple-600 mb-3" />
              <div className="text-3xl font-bold text-[#002FA7]">
                {recentResults.length > 0 
                  ? (recentResults.reduce((a, r) => a + (r.overall_score || 0), 0) / recentResults.length).toFixed(1)
                  : '—'}
              </div>
              <div className="text-sm text-gray-500">Avg Proficiency Score</div>
            </div>
          </div>

          {/* Student Progress Monitoring — dedicated section (owner-approved placement, 2026-10-13).
              Reuses existing /api/classes/my-students + /api/growth/student/{id} and renders a
              MOSAIC-only chronological 4-domain report on /teacher/student-progress. */}
          <div
            className="swiss-card cursor-pointer hover:bg-indigo-50 transition-colors border-2 border-gray-200 mb-8"
            onClick={() => navigate('/teacher/student-progress')}
            data-testid="tile-student-progress-monitoring"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Student Progress Monitoring
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Review each student's MOSAIC assessment history across Listening, Speaking, Reading,
                  and Writing on a chronological chart. Print a per-student progress report.
                </p>
              </div>
              <Button variant="outline" size="sm" className="mt-1" data-testid="tile-student-progress-cta">
                Open Report →
              </Button>
            </div>
          </div>

          {/* TabsList retained as a hidden anchor so Radix internals + any
              external selectors keep working. The visible navigation is
              rendered by <TeacherSidebar/>. */}
          <TabsList className="sr-only" aria-hidden="true">
            <TabsTrigger value="overview" data-testid="tch-overview-tab-anchor">Overview</TabsTrigger>
            <TabsTrigger value="assignments" data-testid="assignments-tab-anchor">Assignments</TabsTrigger>
            <TabsTrigger value="grading" data-testid="pending-grading-tab-anchor">Grading</TabsTrigger>
            <TabsTrigger value="results" data-testid="tch-results-tab-anchor">Recent Results</TabsTrigger>
            <TabsTrigger value="resources" data-testid="teacher-resources-tab-anchor">Teacher Resources</TabsTrigger>
            <TabsTrigger value="classes" data-testid="class-management-tab-anchor">Classes</TabsTrigger>
            <TabsTrigger value="custom-questions" data-testid="custom-questions-tab-anchor">My Questions</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab-anchor">Analytics</TabsTrigger>
            <TabsTrigger value="wida-standards" data-testid="wida-standards-tab-anchor">ELD Standards</TabsTrigger>
            <TabsTrigger value="growth" data-testid="growth-tab-anchor">Growth</TabsTrigger>
            <TabsTrigger value="cohort" data-testid="cohort-tab-anchor">Cohort</TabsTrigger>
            <TabsTrigger value="projections" data-testid="projections-tab-anchor">Projections</TabsTrigger>
            <TabsTrigger value="windows" data-testid="assessment-windows-tab-anchor">Assessment Windows</TabsTrigger>
            <TabsTrigger value="integrity" data-testid="exam-integrity-tab-anchor">Exam Integrity</TabsTrigger>
            <TabsTrigger value="demo-data" data-testid="demo-data-tab-anchor">Demo Data</TabsTrigger>
            <TabsTrigger value="lesson-plans" data-testid="lesson-plans-tab-anchor">Lesson Plans</TabsTrigger>
            <TabsTrigger value="quick-checks" data-testid="quick-checks-tab-anchor">Quick Checks</TabsTrigger>
            <TabsTrigger value="toolkit" data-testid="toolkit-tab-anchor">Toolkit</TabsTrigger>
            <TabsTrigger value="crosswalk" data-testid="crosswalk-tab-anchor">Program Crosswalk</TabsTrigger>
            <TabsTrigger value="class-groups" data-testid="class-groups-tab-anchor">Class Groups</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="feedback-tab-anchor">Beta Feedback</TabsTrigger>
          </TabsList>

          <div className="space-y-6">

        <TabsContent value="overview">
          <div className="space-y-6">
            <ProficiencyWatchDigest canSeeAdmin={['super_admin', 'district_admin'].includes(user?.role)} />
            <OverviewTab classes={classes} pendingGrading={pendingGrading} setActiveTab={setActiveTab} />
          </div>
        </TabsContent>

        <TabsContent value="assignments" data-testid="assignments-content">
          <AssignmentManager classes={classes} level="teacher" />
        </TabsContent>

        <TabsContent value="grading" data-testid="pending-grading-content">
          <GradingTab pendingGrading={pendingGrading} setPendingGrading={setPendingGrading} initialGraded={initialGraded} user={user} />
        </TabsContent>

        <TabsContent value="results">
          <RecentResultsTab recentResults={recentResults} />
        </TabsContent>

        <TabsContent value="resources" data-testid="teacher-resources-content">
          <TeacherResourcesTab />
        </TabsContent>

        <TabsContent value="classes" data-testid="class-management-content">
          <ClassManagement initialView={classInitialView} />
        </TabsContent>

        <TabsContent value="custom-questions" data-testid="custom-questions-content">
          <CustomQuestionCreator />
        </TabsContent>

        <TabsContent value="analytics" data-testid="analytics-content">
          <AnalyticsDashboard />
        </TabsContent>

        <TabsContent value="wida-standards" data-testid="wida-standards-content">
          <ProficiencyTrainingModule />
        </TabsContent>

        <TabsContent value="growth" data-testid="growth-content">
          <div className="mb-6">
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Student Growth Tracking</h3>
            <p className="text-sm text-gray-500">Monitor improvement across assessment windows by class</p>
          </div>
          {classes.length === 0 ? (
            <div className="border-2 border-gray-200 p-12 text-center text-gray-500">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No classes found. Create a class first to track growth.</p>
            </div>
          ) : (
            <div>
              <div className="flex gap-3 mb-6 flex-wrap">
                {classes.map(c => (
                  <Button
                    key={c.class_id}
                    variant={activeGrowthClass === c.class_id ? "default" : "outline"}
                    onClick={() => setActiveGrowthClass(c.class_id)}
                    className={`btn-sharp ${activeGrowthClass === c.class_id ? 'bg-[#002FA7] text-white' : ''}`}
                    data-testid={`growth-class-${c.class_id}`}
                  >
                    {c.name} (Gr. {c.grade_level})
                  </Button>
                ))}
              </div>
              {activeGrowthClass && <GrowthTracker classId={activeGrowthClass} />}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cohort" data-testid="cohort-content">
          <CohortComparison />
        </TabsContent>

        <TabsContent value="projections" data-testid="projections-content">
          {classes.length === 0 ? (
            <div className="border-2 border-gray-200 p-12 text-center text-gray-500">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No classes found. Create a class first to view projections.</p>
            </div>
          ) : (
            <div>
              <div className="flex gap-3 mb-6 flex-wrap">
                {classes.map(c => (
                  <Button
                    key={c.class_id}
                    variant={activeGrowthClass === c.class_id ? "default" : "outline"}
                    onClick={() => setActiveGrowthClass(c.class_id)}
                    className={`btn-sharp ${activeGrowthClass === c.class_id ? 'bg-[#002FA7] text-white' : ''}`}
                    data-testid={`projections-class-${c.class_id}`}
                  >
                    {c.name} (Gr. {c.grade_level})
                  </Button>
                ))}
              </div>
              {activeGrowthClass ? (
                <PredictiveProjections classId={activeGrowthClass} />
              ) : (
                <div className="py-12 text-center text-gray-500">Select a class to view end-of-year projections</div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="windows" data-testid="windows-content">
          <AssessmentWindows />
        </TabsContent>

        <TabsContent value="integrity" data-testid="exam-integrity-content">
          <ExamIntegrityTab flaggedSessions={flaggedSessions} />
        </TabsContent>

        <TabsContent value="demo-data" data-testid="demo-data-content">
          <DemoDataManager />
        </TabsContent>

        <TabsContent value="lesson-plans" data-testid="lesson-plans-content">
          <LessonPlanTab />
        </TabsContent>

        <TabsContent value="quick-checks" data-testid="quick-checks-content">
          <QuickChecksTab classes={classes} />
        </TabsContent>

        <TabsContent value="toolkit" data-testid="toolkit-content">
          <TeacherToolkitTab classes={classes} />
        </TabsContent>

        <TabsContent value="crosswalk" data-testid="crosswalk-content">
          <CrosswalkTool />
        </TabsContent>

        <TabsContent value="class-groups" data-testid="class-groups-content">
          <ClassGroupsReport />
        </TabsContent>

        <TabsContent value="feedback" data-testid="feedback-content">
          <BetaFeedbackSurvey />
        </TabsContent>
          </div>
        </div>
      </Tabs>
      {/* v6.34.15 — Smart install banner (hidden when already installed as PWA). */}
      <InstallAppBanner />
    </div>
  );
};

export default TeacherDashboard;
